// ═══════════════════════════════════════════════════════════════════════════════
// Pairing Success Page — redirect to Consent
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { UserPublic } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { NotificationBell } from '../../components/NotificationBell';
import { MainMenuButton } from '../../components/MainMenuButton';
import { AppButton } from '../../components/ui/AppButton';
import { getPostAuthHomePath } from '../../lib/authRedirect';
import { PLAY_BANNER_ABSOLUTE_TOP_4_CLASS } from '../../lib/layoutChrome';

type PairingSuccessState = {
  pairingId: string;
  employer: UserPublic;
};

export const PairingSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user } = useAuth();
  const state = location.state as PairingSuccessState | null;

  const needsConsentOnboarding = useMemo(
    () => !user?.consentedAt || user.consentedAt.trim() === '',
    [user?.consentedAt]
  );

  const handleContinue = useCallback(() => {
    if (!user) {
      navigate('/consent', { replace: true });
      return;
    }
    navigate(getPostAuthHomePath(user), { replace: true });
  }, [navigate, user]);

  const resolveRoleLabel = (role: string | undefined): string => {
    if (role === 'helper') return t('role.helper');
    if (role === 'employer') return t('role.employer');
    return role ?? '';
  };
  const roleLabel = resolveRoleLabel(state?.employer?.role);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className={`absolute right-4 z-10 flex items-center gap-2 ${PLAY_BANNER_ABSOLUTE_TOP_4_CLASS}`}>
        <MainMenuButton />
        <NotificationBell />
      </div>
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white p-8 shadow-lg">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-10 w-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <h1
              className="text-2xl font-bold text-gray-900"
              role="heading"
              aria-level={1}
            >
              {t('pairing.success.title')}
            </h1>
            <p className="mt-2 text-sm text-gray-500">{t('pairing.success.subtitle')}</p>
          </div>

          {state?.employer && (
            <div className="mb-6 rounded-xl bg-gray-50 p-5">
              <p className="mb-2 text-xs font-medium tracking-wider text-gray-500 uppercase">
                {t('pairing.success.pairedWith')}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
                  <span className="text-lg font-bold text-primary-700">
                    {state.employer.nickname.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {state.employer.nickname}
                  </p>
                  <p className="text-sm text-gray-500">{roleLabel}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex gap-3">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {needsConsentOnboarding
                    ? t('pairing.success.nextTitle')
                    : t('pairing.success.nextTitleReady')}
                </p>
                <p className="mt-1 text-xs text-amber-700">
                  {needsConsentOnboarding
                    ? t('pairing.success.nextBody')
                    : t('pairing.success.nextBodyReady')}
                </p>
              </div>
            </div>
          </div>

          <AppButton
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleContinue}
            aria-label={
              needsConsentOnboarding
                ? t('pairing.success.continueAria')
                : t('pairing.success.continueAppAria')
            }
            className="px-6"
          >
            {needsConsentOnboarding ? t('pairing.success.continue') : t('pairing.success.continueApp')}
          </AppButton>
        </div>
      </div>
    </div>
  );
};
